import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import Styles from './styles.module.scss';
import { Button, Heading, Banner, Select, TextContainer, Modal, Autocomplete, Stack, Tag, Labelled, TextStyle, TextField, Checkbox, Tooltip, ChoiceList } from '@shopify/polaris';
import { CancelSmallMinor } from '@shopify/polaris-icons';
import { FormMode } from '../../helpers/constant';
import { isMarketCatalogPriceUpdateRunning } from '../common/CatalogPriceUpdateBanner';
import productsApi from '../../services/productsApi';
import { Spinner } from '@shopify/polaris';

const MAX_FIXED_AMOUNT = 1_000_000_000; // 10^9

const getMarketSettingsSnapshot = (market) => ({
  setCurrencyCode: market?.setCurrencyCode || '',
  inLocalCurrencies: Boolean(market?.inLocalCurrencies),
});

const normalizeCollectionIds = (ids) => {
  if (Array.isArray(ids)) {
    return ids.map((id) => String(id)).filter(Boolean).sort();
  }
  if (typeof ids === 'string' && ids.trim()) {
    return ids.split(',').map((id) => id.trim()).filter(Boolean).sort();
  }
  return [];
};

const getCatalogSnapshot = (
  enabled,
  type,
  operation,
  value,
  isComparePriceInclude,
  isPriceSetAccordingToPrimaryMarket,
  productScope,
  collectionIds,
) => ({
  enabled: Boolean(enabled),
  type: type === 'fixed' ? 'fixed' : 'percentage',
  operation: operation === 'decrease' ? 'decrease' : 'increase',
  value: value != null && value !== '' ? String(value) : '',
  isComparePriceInclude: isComparePriceInclude !== false,
  isPriceSetAccordingToPrimaryMarket: Boolean(isPriceSetAccordingToPrimaryMarket),
  productScope: productScope === 'collection' ? 'collection' : 'all',
  collectionIds: normalizeCollectionIds(collectionIds).join(','),
});

const wrapWithTooltip = (tooltip, node) => {
  if (!tooltip) return node;
  return (
    <Tooltip content={tooltip} dismissOnMouseOut>
      <span style={{ display: 'block' }}>{node}</span>
    </Tooltip>
  );
};

const MarketForm = ({ defaultCountryOptions, onSave, formMode, onCloseForm, marketForm, onFormStateChange,
  isWarningModelOpen, openModal, closeModal, tmpSetCurrency, handleDeleteRegion, openConfirmationDelete, isConfirmationModalOpen,
  timer, closeConfirmationModal, deleteMarketApiCalled, onSaveApiCalled, setCurrencyApiCalled, setCurrencyOptions, displayCurrencyApiCalled,
  displayCurrencyOptions, updateDisplayCurrencyText, displayCurrencyInputValue, removeDisplayCurrencies, managedBySPBanner, supportedCheckoutCurrencies, isROWAvailable,
  onCatalogPriceError, catalogPriceUpdateRunningMarkets = [] }) => {

  const [adjustPricesEnabled, setAdjustPricesEnabled] = useState(false);
  const [catalogPriceType, setCatalogPriceType] = useState('percentage');
  const [catalogPriceOperation, setCatalogPriceOperation] = useState('increase');
  const [catalogPriceValue, setCatalogPriceValue] = useState('');
  const [catalogComparePriceInclude, setCatalogComparePriceInclude] = useState(true);
  const [priceSetAccordingTo, setPriceSetAccordingTo] = useState('current');
  const [productScope, setProductScope] = useState('all');
  const [selectedCollectionIds, setSelectedCollectionIds] = useState([]);
  const [draftCollectionIds, setDraftCollectionIds] = useState([]);
  const [availableCollections, setAvailableCollections] = useState([]);
  const [collectionSearch, setCollectionSearch] = useState('');
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [collectionsError, setCollectionsError] = useState('');
  const [collectionPickerOpen, setCollectionPickerOpen] = useState(false);
  const [catalogPriceFieldError, setCatalogPriceFieldError] = useState('');
  const initialMarketSettingsRef = useRef(null);
  const initialCatalogSnapshotRef = useRef(null);

  // Shopify market GID from store-markets API (e.g. gid://shopify/Market/35573858558)
  const shopifyMarketId = marketForm.market_id || marketForm.marketId || null;
  const showCatalogPriceAdjustment =
    formMode === FormMode.EDIT && Boolean(shopifyMarketId);

  const isPrimaryMarketEdit =
    formMode === FormMode.EDIT && Boolean(marketForm.isPrimaryMarket);

  useEffect(() => {
    const adj = marketForm.catalogPriceAdjustments;
    const isPrimary = formMode === FormMode.EDIT && Boolean(marketForm.isPrimaryMarket);
    if (adj && (adj.type || adj.operation || adj.value != null)) {
      const type = adj.type === 'fixed' ? 'fixed' : 'percentage';
      const operation = adj.operation === 'decrease' ? 'decrease' : 'increase';
      const value = adj.value != null && adj.value !== '' ? String(adj.value) : '';
      const isComparePriceInclude = adj.isComparePriceInclude !== false;
      const collectionIds = normalizeCollectionIds(adj.collectionIds);
      const isPriceSetAccordingToPrimaryMarket = isPrimary
        ? false
        : adj.isPriceSetAccordingToPrimaryMarket === true;
      const accordingTo = isPrimary
        ? 'primary'
        : (isPriceSetAccordingToPrimaryMarket ? 'primary' : 'current');
      const nextProductScope = collectionIds.length > 0 ? 'collection' : 'all';
      setAdjustPricesEnabled(true);
      setCatalogPriceType(type);
      setCatalogPriceOperation(operation);
      setCatalogPriceValue(value);
      setCatalogComparePriceInclude(isComparePriceInclude);
      setPriceSetAccordingTo(accordingTo);
      setProductScope(nextProductScope);
      setSelectedCollectionIds(collectionIds);
      initialCatalogSnapshotRef.current = getCatalogSnapshot(
        true,
        type,
        operation,
        value,
        isComparePriceInclude,
        isPriceSetAccordingToPrimaryMarket,
        nextProductScope,
        collectionIds,
      );
    } else if (formMode === FormMode.EDIT) {
      setAdjustPricesEnabled(false);
      setCatalogPriceType('percentage');
      setCatalogPriceOperation('increase');
      setCatalogPriceValue('');
      setCatalogComparePriceInclude(true);
      setPriceSetAccordingTo(isPrimary ? 'primary' : 'current');
      setProductScope('all');
      setSelectedCollectionIds([]);
      initialCatalogSnapshotRef.current = getCatalogSnapshot(
        false,
        'percentage',
        'increase',
        '',
        true,
        false,
        'all',
        [],
      );
    }
    if (formMode === FormMode.EDIT) {
      initialMarketSettingsRef.current = getMarketSettingsSnapshot(marketForm);
    }
    setCatalogPriceFieldError('');
  }, [marketForm.id, marketForm.catalogPriceAdjustments, formMode]);

  const isSaveBtnEnabled = isPrimaryMarketEdit
    ? adjustPricesEnabled && String(catalogPriceValue).trim().length > 0
    : Boolean(marketForm.regionName && marketForm.setCurrencyCode);
  const isCatalogUpdateRunningForMarket = isMarketCatalogPriceUpdateRunning(
    marketForm.regionName,
    catalogPriceUpdateRunningMarkets,
  );
  const isSaveDisabled =
    !isSaveBtnEnabled || onSaveApiCalled || isCatalogUpdateRunningForMarket;
  const titleMarkup = formMode === FormMode.ADD ? 'Create a new market' : `Market: ${marketForm.regionName}`

  const textField = (
    <Autocomplete.TextField
      onChange={updateDisplayCurrencyText}
      value={displayCurrencyInputValue}
      placeholder="Select display currencies"
    />
  );

  const fetchSupportedCurrencies = () => {
    const supportedCurrencies = [];
    for (let c of marketForm.displayCurrencyCodes) {
      if (supportedCheckoutCurrencies.indexOf(c) >= 0) {
        supportedCurrencies.push(c);
      }
    }
    return supportedCurrencies;
  }

  const validateCatalogPriceValue = (valueStr, type) => {
    const trimmed = String(valueStr).trim();
    if (!trimmed) {
      return type === 'percentage'
        ? 'Please enter a percentage between 1 and 500.'
        : `Please enter a fixed amount between 1 and ${MAX_FIXED_AMOUNT.toLocaleString()}.`;
    }
    const num = parseFloat(trimmed);
    if (type === 'percentage') {
      if (Number.isNaN(num) || num < 1 || num > 500) {
        return 'Please enter a percentage between 1 and 500.';
      }
    } else if (Number.isNaN(num) || num < 1 || num > MAX_FIXED_AMOUNT) {
      return `Please enter a fixed amount between 1 and ${MAX_FIXED_AMOUNT.toLocaleString()}.`;
    }
    return '';
  };

  const handleCatalogPriceValueChange = (value) => {
    setCatalogPriceValue(value);
    if (adjustPricesEnabled) {
      setCatalogPriceFieldError(validateCatalogPriceValue(value, catalogPriceType));
    }
  };

  const handleCatalogPriceTypeChange = (type) => {
    setCatalogPriceType(type);
    if (adjustPricesEnabled && catalogPriceValue) {
      setCatalogPriceFieldError(validateCatalogPriceValue(catalogPriceValue, type));
    } else {
      setCatalogPriceFieldError('');
    }
  };

  const handleAdjustPricesToggle = (checked) => {
    setAdjustPricesEnabled(checked);
    if (!checked) {
      setCatalogPriceFieldError('');
    }
  };

  const isMarketSettingsDirty = () => {
    const initial = initialMarketSettingsRef.current;
    if (!initial) return false;
    const current = getMarketSettingsSnapshot(marketForm);
    return (
      initial.setCurrencyCode !== current.setCurrencyCode ||
      initial.inLocalCurrencies !== current.inLocalCurrencies
    );
  };

  const getIsPriceSetAccordingToPrimaryMarket = () => {
    if (isPrimaryMarketEdit) return false;
    return priceSetAccordingTo === 'primary';
  };

  const priceAccordingToOptions = isPrimaryMarketEdit
    ? [{ value: 'primary', label: 'Primary market' }]
    : [
      { value: 'current', label: 'Current market' },
      { value: 'primary', label: 'Primary market' },
    ];

  const productScopeOptions = [
    { value: 'all', label: 'All products' },
    { value: 'collection', label: 'By collection' },
  ];

  const loadCollections = useCallback(async () => {
    setCollectionsLoading(true);
    setCollectionsError('');
    try {
      const res = await productsApi.getShopProductUniques();
      const body = res?.data ?? res;
      const collections = body?.data?.uniqueCollections ?? [];
      setAvailableCollections(
        Array.isArray(collections)
          ? collections
            .filter((collection) => collection?.id && collection?.name)
            .map((collection) => ({
              label: collection.name,
              value: String(collection.id),
            }))
          : [],
      );
    } catch {
      setAvailableCollections([]);
      setCollectionsError('Unable to load collections. Please try again.');
    } finally {
      setCollectionsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (
      productScope === 'collection' &&
      availableCollections.length === 0 &&
      !collectionsLoading
    ) {
      loadCollections();
    }
  }, [productScope, availableCollections.length, collectionsLoading, loadCollections]);

  const openCollectionPicker = useCallback(() => {
    setDraftCollectionIds(selectedCollectionIds);
    setCollectionSearch('');
    setCollectionPickerOpen(true);
    if (availableCollections.length === 0 && !collectionsLoading) {
      loadCollections();
    }
  }, [
    availableCollections.length,
    collectionsLoading,
    loadCollections,
    selectedCollectionIds,
  ]);

  const closeCollectionPicker = useCallback(() => {
    setCollectionPickerOpen(false);
    setCollectionSearch('');
  }, []);

  const applyCollectionPicker = useCallback(() => {
    setSelectedCollectionIds(draftCollectionIds);
    setCollectionPickerOpen(false);
    setCollectionSearch('');
  }, [draftCollectionIds]);

  const handleProductScopeChange = (value) => {
    setProductScope(value);
    if (value !== 'collection') {
      setSelectedCollectionIds([]);
    } else if (selectedCollectionIds.length === 0) {
      openCollectionPicker();
    }
  };

  const isCatalogAdjustmentDirty = () => {
    // This checkbox is UI-only. When it is off, Save must use only the
    // store-market API and must not lock the currency settings.
    if (!adjustPricesEnabled) return false;

    const initial = initialCatalogSnapshotRef.current;
    const current = getCatalogSnapshot(
      adjustPricesEnabled,
      catalogPriceType,
      catalogPriceOperation,
      catalogPriceValue,
      catalogComparePriceInclude,
      getIsPriceSetAccordingToPrimaryMarket(),
      productScope,
      productScope === 'collection' ? selectedCollectionIds : [],
    );
    if (!initial) {
      return Boolean(adjustPricesEnabled);
    }
    return (
      initial.enabled !== current.enabled ||
      initial.type !== current.type ||
      initial.operation !== current.operation ||
      initial.value !== current.value ||
      initial.isComparePriceInclude !== current.isComparePriceInclude ||
      initial.isPriceSetAccordingToPrimaryMarket !== current.isPriceSetAccordingToPrimaryMarket ||
      initial.productScope !== current.productScope ||
      initial.collectionIds !== current.collectionIds
    );
  };

  const marketSettingsDirty = isMarketSettingsDirty();
  const catalogAdjustmentDirty = isCatalogAdjustmentDirty();

  // Keep the two save operations separate within a single edit session.
  // Save currency changes before editing the catalog adjustment, and vice versa.
  const lockCatalogSection = marketSettingsDirty;
  const lockMarketSettings = catalogAdjustmentDirty && !marketSettingsDirty;
  const marketSettingsSaveFirstTooltip =
    'Please save first, then you can change currency settings.';
  const catalogSaveFirstTooltip =
    'Please save first, then you will be able to increase / decrease.';

  const catalogAdjustmentDisabled =
    !adjustPricesEnabled || onSaveApiCalled || lockCatalogSection;

  const isSetCurrencyDisabled =
    isPrimaryMarketEdit || setCurrencyApiCalled || lockMarketSettings;
  const isLocalCurrencyDisabled =
    isPrimaryMarketEdit || displayCurrencyApiCalled || lockMarketSettings;
  const isAdjustCheckboxDisabled = onSaveApiCalled;

  const selectedCollectionLabels = availableCollections
    .filter((collection) => selectedCollectionIds.includes(collection.value))
    .map((collection) => collection.label);
  const filteredCollections = availableCollections.filter((collection) =>
    collection.label.toLowerCase().includes(collectionSearch.toLowerCase()),
  );
  // Keep labels for already-selected ids even if collections haven't loaded yet.
  const selectedCollectionSummary =
    selectedCollectionLabels.length > 0
      ? selectedCollectionLabels
      : selectedCollectionIds;

  const buildCatalogAdjustment = () => {
    // The checkbox controls whether the additional catalog API is called.
    // Market settings are always saved by the parent save flow.
    if (!showCatalogPriceAdjustment || !adjustPricesEnabled) {
      return null;
    }
    const validationError = validateCatalogPriceValue(catalogPriceValue, catalogPriceType);
    if (validationError) {
      setCatalogPriceFieldError(validationError);
      return { error: validationError };
    }
    if (!shopifyMarketId) {
      return { error: 'Shopify market ID is missing for this market.' };
    }
    if (productScope === 'collection' && selectedCollectionIds.length === 0) {
      return { error: 'Please select at least one collection.' };
    }
    const value = parseFloat(catalogPriceValue);
    const payload = {
      marketId: String(shopifyMarketId),
      type: catalogPriceType,
      operation: catalogPriceOperation,
      value: Number(value),
      isComparePriceInclude: Boolean(catalogComparePriceInclude),
      isPriceSetAccordingToPrimaryMarket: getIsPriceSetAccordingToPrimaryMarket(),
    };
    if (productScope === 'collection') {
      payload.collectionIds = selectedCollectionIds.join(',');
    }
    return payload;
  };

  const handleSaveClick = () => {
    const catalogAdjustment = buildCatalogAdjustment();
    if (catalogAdjustment?.error) {
      onCatalogPriceError && onCatalogPriceError(catalogAdjustment.error);
      return;
    }
    onSave(catalogAdjustment, marketSettingsDirty);
  };

  const isCollectionNamesLoaded =
    selectedCollectionIds.length === 0 ||
    selectedCollectionSummary.every(
      name => !name.startsWith("gid://shopify/Collection/")
    );

  return (
    <>
      <div className={`${Styles['backdrop']}`}></div>
      <div className={Styles['container']}>
        <div className={`${Styles['header']} align-items-center d-flex justify-content-between`}>
          <Heading element="h1">{titleMarkup}</Heading>
          <div>
            <Button onClick={onCloseForm} plain icon={CancelSmallMinor}></Button>
          </div>
        </div>
        <div className={`${Styles['body']} marketForm-main`}>
          {formMode === FormMode.ADD && <div className="mb-35">
            <Labelled
              label={
                <>
                  <div className='Polaris-Heading'>Choose the country/continent</div>
                </>
              }
            >
            </Labelled>

            <div className="row">
              <div className="cl-12">
                <Select
                  placeholder="Select"
                  options={defaultCountryOptions}
                  onChange={(e) => { onFormStateChange('regionName', e) }}
                  value={marketForm.regionName}
                />
              </div>
            </div>
          </div>}
          <div className="mb-35">
            <Labelled
              label={
                <>
                  <div className='Polaris-Heading'>Choose the currency in which you want to set the prices</div>
                </>
              }
            >
            </Labelled>
            <div className="row">
              <div className="cl-12">
                {wrapWithTooltip(
                  lockMarketSettings ? marketSettingsSaveFirstTooltip : null,
                  <Select
                    key="setCurrency"
                    placeholder="Select"
                    options={setCurrencyOptions}
                    onChange={(e) => { formMode === FormMode.ADD ? onFormStateChange('setCurrencyCode', e) : openModal(e) }}
                    value={marketForm.setCurrencyCode}
                    disabled={isSetCurrencyDisabled}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="mb-35">
            <Labelled
              label={
                <>
                  <div className='Polaris-Heading'>Let customers see in their region's currency</div>
                </>
              }
            >
            </Labelled>

            <div className="row">
              <div className="cl-12">
                {wrapWithTooltip(
                  lockMarketSettings ? marketSettingsSaveFirstTooltip : null,
                  <Select
                    key="inLocalCurrencies"
                    placeholder="Select"
                    options={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]}
                    onChange={(e) => { onFormStateChange('inLocalCurrencies', e) }}
                    value={marketForm.inLocalCurrencies ? "yes" : "no"}
                    disabled={isLocalCurrencyDisabled}
                  />
                )}
              </div>
            </div>
          </div>

          {showCatalogPriceAdjustment && (
            <div className="mb-35">
              {wrapWithTooltip(
                null,
                <Checkbox
                  label="Adjust prices in this market"
                  checked={adjustPricesEnabled}
                  onChange={handleAdjustPricesToggle}
                  disabled={isAdjustCheckboxDisabled}
                />
              )}
              <div style={{ marginLeft: 28, marginTop: 4 }}>
                <TextStyle variation="subdued">
                  <span className="mediumFontSized plainFonts">
                    Bulk adjust product prices for this market by percentage or fixed amount.
                  </span>
                </TextStyle>
              </div>
              <div
                style={{
                  marginLeft: 28,
                  marginTop: 12,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  flexWrap: 'nowrap',
                }}
              >
                <span
                  className="mediumFontSized plainFonts"
                  style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  Update price according to
                </span>
                <div style={{ width: 225, flexShrink: 0 }}>
                  {wrapWithTooltip(
                    lockCatalogSection ? catalogSaveFirstTooltip : null,
                    <Select
                      labelHidden
                      label="Update price according to"
                      options={priceAccordingToOptions}
                      value={priceSetAccordingTo}
                      onChange={setPriceSetAccordingTo}
                      disabled={catalogAdjustmentDisabled || isPrimaryMarketEdit}
                    />
                  )}
                </div>
              </div>
              <div
                className={Styles['alignedSelectButtonRow']}
                style={{
                  marginLeft: 28,
                  marginTop: 12,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  flexWrap: 'nowrap',
                }}
              >
                <span
                  className="mediumFontSized plainFonts"
                  style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  Apply to
                </span>
                <div style={{ width: 170, flexShrink: 0 }}>
                  {wrapWithTooltip(
                    lockCatalogSection ? catalogSaveFirstTooltip : null,
                    <Select
                      labelHidden
                      label="Apply to"
                      options={productScopeOptions}
                      value={productScope}
                      onChange={handleProductScopeChange}
                      disabled={catalogAdjustmentDisabled}
                    />
                  )}
                </div>
                {productScope === 'collection' ? (
                  <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                    <Button
                      onClick={openCollectionPicker}
                      disabled={catalogAdjustmentDisabled}
                    >
                      {selectedCollectionIds.length > 0
                        ? 'Edit collections'
                        : 'Select collections'}
                    </Button>
                  </div>
                ) : null}
              </div>
              {productScope === 'collection' && selectedCollectionIds.length > 0 ? (
                <div style={{ marginLeft: 28, marginTop: 10 }}>
                  <Banner status="info">
                    <p>
                      <span className="bold-font">
                        {selectedCollectionIds.length} collection
                        {selectedCollectionIds.length === 1 ? '' : 's'} selected:
                      </span>{' '}
                      {isCollectionNamesLoaded ? (
                        selectedCollectionSummary.join(', ')
                      ) : (
                        <Spinner
                          size="small"
                          accessibilityLabel="Loading collection names"
                        />
                      )}
                    </p>
                  </Banner>
                </div>
              ) : null}
              <div className="mt-15" style={{ marginLeft: 28 }}>
                <Stack vertical spacing="loose">
                  <Stack spacing="tight">
                    {wrapWithTooltip(
                      lockCatalogSection ? catalogSaveFirstTooltip : null,
                      <Button
                        pressed={catalogPriceOperation === 'increase'}
                        onClick={() => setCatalogPriceOperation('increase')}
                        disabled={catalogAdjustmentDisabled}
                      >
                        Increase
                      </Button>
                    )}
                    {wrapWithTooltip(
                      lockCatalogSection ? catalogSaveFirstTooltip : null,
                      <Button
                        pressed={catalogPriceOperation === 'decrease'}
                        onClick={() => setCatalogPriceOperation('decrease')}
                        disabled={catalogAdjustmentDisabled}
                      >
                        Decrease
                      </Button>
                    )}
                  </Stack>

                  {wrapWithTooltip(
                    lockCatalogSection ? catalogSaveFirstTooltip : null,
                    <Select
                      label="Adjustment type"
                      options={[
                        { value: 'percentage', label: 'By percentage' },
                        { value: 'fixed', label: 'By fixed amount' },
                      ]}
                      value={catalogPriceType}
                      onChange={handleCatalogPriceTypeChange}
                      disabled={catalogAdjustmentDisabled}
                    />
                  )}

                  {wrapWithTooltip(
                    lockCatalogSection ? catalogSaveFirstTooltip : null,
                    <TextField
                      label={catalogPriceType === 'percentage' ? 'Percentage' : 'Fixed amount'}
                      type="number"
                      min={1}
                      max={catalogPriceType === 'percentage' ? 500 : MAX_FIXED_AMOUNT}
                      value={catalogPriceValue}
                      onChange={handleCatalogPriceValueChange}
                      onBlur={() => {
                        if (!adjustPricesEnabled || lockCatalogSection) return;
                        setCatalogPriceFieldError(
                          validateCatalogPriceValue(catalogPriceValue, catalogPriceType)
                        );
                      }}
                      placeholder={catalogPriceType === 'percentage' ? 'e.g. 10' : 'e.g. 5'}
                      suffix={catalogPriceType === 'percentage' ? '%' : undefined}
                      error={adjustPricesEnabled && !lockCatalogSection ? catalogPriceFieldError : undefined}
                      disabled={catalogAdjustmentDisabled}
                    />
                  )}

                  {wrapWithTooltip(
                    lockCatalogSection ? catalogSaveFirstTooltip : null,
                    <Checkbox
                      label="Apply to compare-at prices"
                      checked={catalogComparePriceInclude}
                      onChange={setCatalogComparePriceInclude}
                      disabled={catalogAdjustmentDisabled}
                    />
                  )}
                </Stack>
              </div>
            </div>
          )}

          {showCatalogPriceAdjustment && (
            <div className="mb-20">
              <Banner status="info">
                <p>
                  <span className="bold-font">Note:</span> If a decrease adjustment brings any price below 0, the price will be set to 0. Percentage adjustments will only apply to prices greater than 0.
                </p>
              </Banner>
            </div>
          )}

          {isPrimaryMarketEdit && (
            <div className="mb-20">
              <Banner status="info">
                <p>
                  Primary market settings are read-only here. You can only update bulk price adjustment (increase / decrease) above.
                </p>
              </Banner>
            </div>
          )}
        </div>
        <div className={`${Styles['footer']}`}>
          <div className={`${Styles['subDiv']}`}>
            {formMode === FormMode.EDIT && !marketForm.isPrimaryMarket && <div className={`${Styles['button1']} marketDeleteBtn mr-15`}>
              <Button monochrome outline onClick={() => openConfirmationDelete()}>
                Delete
              </Button>
            </div>}
            <div className="mr-15">
              <Button onClick={onCloseForm}> Cancel</Button>
            </div>
            <div className="marketformPrimaryBtn">
              {isCatalogUpdateRunningForMarket ? (
                <Tooltip content="Already in progress" dismissOnMouseOut>
                  <span className="uploadCsvButtonWrap">
                    <Button
                      loading={onSaveApiCalled}
                      disabled={isSaveDisabled}
                      primary
                      onClick={handleSaveClick}
                    >
                      Save
                    </Button>
                  </span>
                </Tooltip>
              ) : (
                <Button
                  loading={onSaveApiCalled}
                  disabled={isSaveDisabled}
                  primary
                  onClick={handleSaveClick}
                >
                  Save
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal
        open={isWarningModelOpen}
        onClose={closeModal}
        title={"Are you sure you want to change?"}
        primaryAction={{
          content: 'Yes',
          onAction: () => { onFormStateChange('setCurrencyCode', tmpSetCurrency); closeModal() },
        }}
        secondaryActions={[
          {
            content: 'No',
            onAction: closeModal,
          },
        ]}
      >
        <Modal.Section>
          <TextContainer>
            <p>
              Changing currency used to set the prices in this dashboard will change all your product prices.
            </p>
          </TextContainer>
        </Modal.Section>
      </Modal>

      <Modal
        open={isConfirmationModalOpen}
        onClose={closeConfirmationModal}
        title={timer > 2000 ? `Your application will freeze for ${timer / 1000} seconds` : `Do you want to delete ${marketForm.regionName} market?`}
        primaryAction={{
          content: 'Yes',
          loading: deleteMarketApiCalled,
          disabled: deleteMarketApiCalled,
          onAction: () => { handleDeleteRegion() },
        }}
        secondaryActions={[
          {
            content: 'No',
            onAction: closeConfirmationModal,
          },
        ]}
      >
        <Modal.Section>
          <p>Are you sure want to delete this market? This will remove all the prices you set up for this market permanently. </p>
        </Modal.Section>
      </Modal>
      <Modal
        open={collectionPickerOpen}
        onClose={closeCollectionPicker}
        title="Select collections"
        primaryAction={{
          content: 'Apply',
          onAction: applyCollectionPicker,
          disabled: draftCollectionIds.length === 0,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: closeCollectionPicker,
          },
        ]}
      >
        <Modal.Section>
          <Stack vertical spacing="loose">
            <TextContainer>
              <p>Choose which collections this price adjustment should apply to.</p>
            </TextContainer>

            {collectionsError ? (
              <Banner
                status="critical"
                action={{ content: 'Retry', onAction: loadCollections }}
              >
                <p>{collectionsError}</p>
              </Banner>
            ) : null}

            {draftCollectionIds.length > 0 ? (
              <Banner status="info">
                <p>
                  <span className="bold-font">
                    {draftCollectionIds.length} collection
                    {draftCollectionIds.length === 1 ? '' : 's'} selected:
                  </span>{' '}
                  {availableCollections
                    .filter((collection) => draftCollectionIds.includes(collection.value))
                    .map((collection) => collection.label)
                    .join(', ') || draftCollectionIds.join(', ')}
                </p>
              </Banner>
            ) : null}

            <div style={{ marginTop: 4 }}>
              <TextField
                label="Search collections"
                value={collectionSearch}
                onChange={setCollectionSearch}
                placeholder="Search by collection name"
                clearButton
                onClearButtonClick={() => setCollectionSearch('')}
                disabled={collectionsLoading}
              />
            </div>

            {collectionsLoading ? (
              <TextContainer>
                <p>Loading collections…</p>
              </TextContainer>
            ) : null}

            {!collectionsLoading &&
              !collectionsError &&
              availableCollections.length === 0 ? (
              <Banner status="info">
                <p>No collections are available for this store.</p>
              </Banner>
            ) : null}

            {!collectionsLoading &&
              availableCollections.length > 0 &&
              filteredCollections.length === 0 ? (
              <Banner status="info">
                <p>No collections match “{collectionSearch}”.</p>
              </Banner>
            ) : null}

            {filteredCollections.length > 0 ? (
              <div style={{ marginTop: 8 }}>
                <div
                  style={{
                    maxHeight: 260,
                    overflowY: 'auto',
                    border: '1px solid #dfe3e8',
                    borderRadius: 4,
                    padding: '0 12px',
                  }}
                >
                  <ChoiceList
                    title="Select collections"
                    allowMultiple
                    choices={filteredCollections}
                    selected={draftCollectionIds}
                    onChange={setDraftCollectionIds}
                    disabled={collectionsLoading}
                  />
                </div>
              </div>
            ) : null}
          </Stack>
        </Modal.Section>
      </Modal>
    </>
  );
};

MarketForm.propTypes = {
  marketForm: PropTypes.object.isRequired
};

export default MarketForm;
